import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, UserCheck, UserX, Users, Trash2, Edit, Lock, Eye, EyeOff, Monitor } from 'lucide-react';
import DeviceManagement from '../components/DeviceManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsuarios = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ nome: '', email: '', papel: '', status: '' });
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterPapel, setFilterPapel] = useState('todos');
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [passwordFormData, setPasswordFormData] = useState({ novaSenha: '', confirmarSenha: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [viewingDevicesUser, setViewingDevicesUser] = useState(null);

  useEffect(() => {
    fetchUsuarios();
  }, [filterStatus, filterPapel]);

  const fetchUsuarios = async () => {
    try {
      const response = await axiosInstance.get(`${API}/admin/usuarios`, {
        params: {
          status: filterStatus === 'todos' ? 'all' : filterStatus,
          papel: filterPapel === 'todos' ? 'all' : filterPapel,
          q: '',
          page: 1,
          pageSize: 50
        }
      });
      setUsuarios(response.data.items || response.data);
      setTotal(response.data.total || response.data.length);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axiosInstance.put(`${API}/admin/usuarios/${userId}/status`, { status: newStatus });
      toast.success(`Status alterado para ${newStatus}`);
      fetchUsuarios();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handlePapelChange = async (userId, newPapel) => {
    if (!window.confirm(`Tem certeza que deseja mudar o papel deste usuário para ${newPapel}?`)) return;
    
    try {
      await axiosInstance.put(`${API}/admin/usuarios/${userId}`, { papel: newPapel });
      toast.success(`Papel alterado para ${newPapel}`);
      fetchUsuarios();
    } catch (error) {
      toast.error('Erro ao alterar papel');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) return;

    try {
      await axiosInstance.delete(`${API}/admin/usuarios/${userId}`);
      toast.success('Usuário excluído com sucesso');
      fetchUsuarios();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleEditClick = (usuario) => {
    setEditingUser(usuario);
    setEditFormData({
      nome: usuario.nome,
      email: usuario.email,
      papel: usuario.papel,
      status: usuario.status
    });
  };

  const handleEditSubmit = async () => {
    try {
      await axiosInstance.put(`${API}/admin/usuarios/${editingUser.id}`, editFormData);
      toast.success('Usuário atualizado com sucesso');
      setEditingUser(null);
      fetchUsuarios();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleResetPasswordClick = (usuario) => {
    setResetPasswordUser(usuario);
    setPasswordFormData({ novaSenha: '', confirmarSenha: '' });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleResetPasswordSubmit = async () => {
    // Validações
    if (!passwordFormData.novaSenha || !passwordFormData.confirmarSenha) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (passwordFormData.novaSenha.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (passwordFormData.novaSenha !== passwordFormData.confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }

    try {
      await axiosInstance.post(`${API}/admin/usuarios/${resetPasswordUser.id}/set-password`, {
        novaSenha: passwordFormData.novaSenha
      });
      toast.success('Senha redefinida com sucesso');
      setResetPasswordUser(null);
      setPasswordFormData({ novaSenha: '', confirmarSenha: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    }
  };

  const handleVoltar = () => {
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendente: 'bg-yellow-500 text-white',
      ativo: 'bg-green-600 text-white',
      bloqueado: 'bg-red-600 text-white'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getPapelBadge = (papel) => {
    const styles = {
      admin: 'bg-[#CF0D1D] text-white',
      medico: 'bg-blue-600 text-white',
      ator: 'bg-purple-600 text-white'
    };
    const icons = {
      admin: '👑',
      medico: '⚕️',
      ator: '🎭'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[papel]} flex items-center gap-1`}>
        <span>{icons[papel]}</span>
        {papel}
      </span>
    );
  };

  const filteredUsuarios = usuarios.filter(u => {
    if (filterStatus !== 'todos' && u.status !== filterStatus) return false;
    if (filterPapel !== 'todos' && u.papel !== filterPapel) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Button
            onClick={handleVoltar}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-2"
            data-testid="voltar-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Gerenciar Usuários</h1>
              <p className="text-gray-400 text-sm mt-1">
                {filteredUsuarios.length} {filteredUsuarios.length === 1 ? 'usuário' : 'usuários'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="bg-white border-0 mb-6 rounded-xl shadow-lg">
          <CardHeader>
            <CardTitle className="text-[#111111] text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label className="text-[#111111] text-sm mb-2 block">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-[#111111] text-sm mb-2 block">Papel</Label>
                <Select value={filterPapel} onValueChange={setFilterPapel}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="medico">Médico</SelectItem>
                    <SelectItem value="ator">Ator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-white border-0 rounded-xl shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="usuarios-table">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Papel</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Criado em</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsuarios.map((usuario) => (
                  <tr key={usuario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{usuario.nome}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{usuario.email}</td>
                    <td className="px-6 py-4">{getPapelBadge(usuario.papel)}</td>
                    <td className="px-6 py-4">{getStatusBadge(usuario.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(usuario.criadoEm).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {/* Status Actions */}
                        {usuario.status === 'pendente' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusChange(usuario.id, 'ativo')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`approve-${usuario.id}`}
                          >
                            <UserCheck className="w-3 h-3 mr-1" />
                            Aprovar
                          </Button>
                        )}
                        {usuario.status === 'ativo' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusChange(usuario.id, 'bloqueado')}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid={`block-${usuario.id}`}
                          >
                            <UserX className="w-3 h-3 mr-1" />
                            Bloquear
                          </Button>
                        )}
                        {usuario.status === 'bloqueado' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusChange(usuario.id, 'ativo')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`activate-${usuario.id}`}
                          >
                            <UserCheck className="w-3 h-3 mr-1" />
                            Ativar
                          </Button>
                        )}
                        
                        {/* Papel Actions - Removido: plataforma não usa mais Ator */}
                        
                        <Button
                          size="sm"
                          onClick={() => handleEditClick(usuario)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`edit-${usuario.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResetPasswordClick(usuario)}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`reset-password-${usuario.id}`}
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          Senha
                        </Button>
                        {usuario.papel !== 'admin' && (
                          <Button
                            size="sm"
                            onClick={() => setViewingDevicesUser(usuario)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            data-testid={`devices-${usuario.id}`}
                          >
                            <Monitor className="w-3 h-3 mr-1" />
                            Dispositivos
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleDeleteUser(usuario.id)}
                          className="bg-[#FF3B3B] hover:bg-[#E22F2F] text-white"
                          data-testid={`delete-${usuario.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Dialog Editar Usuário */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-[#1a1a1a] border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Usuário</DialogTitle>
            <DialogDescription className="text-gray-400">
              Faça as alterações necessárias no usuário
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nome" className="text-gray-200">Nome</Label>
              <Input
                id="edit-nome"
                value={editFormData.nome}
                onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })}
                className="bg-[#2a2a2a] border-gray-600 text-white placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-gray-200">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className="bg-[#2a2a2a] border-gray-600 text-white placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-papel" className="text-gray-200">Papel</Label>
              <Select
                value={editFormData.papel}
                onValueChange={(value) => setEditFormData({ ...editFormData, papel: value })}
              >
                <SelectTrigger className="bg-[#2a2a2a] border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] border-gray-600">
                  <SelectItem value="admin" className="text-white hover:bg-gray-700">Admin</SelectItem>
                  <SelectItem value="medico" className="text-white hover:bg-gray-700">Médico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status" className="text-gray-200">Status</Label>
              <Select
                value={editFormData.status}
                onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
              >
                <SelectTrigger className="bg-[#2a2a2a] border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] border-gray-600">
                  <SelectItem value="pendente" className="text-white hover:bg-gray-700">Pendente</SelectItem>
                  <SelectItem value="ativo" className="text-white hover:bg-gray-700">Ativo</SelectItem>
                  <SelectItem value="bloqueado" className="text-white hover:bg-gray-700">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingUser(null)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditSubmit}
              className="bg-[#FF3B3B] hover:bg-[#E22F2F] text-white"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Redefinir Senha */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => setResetPasswordUser(null)}>
        <DialogContent className="bg-[#1a1a1a] border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#FF3B3B]" />
              Redefinir Senha do Usuário
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Defina uma nova senha para o usuário. As senhas devem coincidir.
            </DialogDescription>
          </DialogHeader>
          
          {resetPasswordUser && (
            <div className="space-y-4 py-4">
              {/* Informações do usuário (somente leitura) */}
              <div className="space-y-2">
                <Label className="text-gray-200">Nome</Label>
                <Input
                  value={resetPasswordUser.nome}
                  disabled
                  className="bg-gray-700 border-gray-600 text-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-200">Email</Label>
                <Input
                  value={resetPasswordUser.email}
                  disabled
                  className="bg-gray-700 border-gray-600 text-gray-300"
                />
              </div>

              {/* Campos de senha */}
              <div className="space-y-2">
                <Label htmlFor="nova-senha" className="text-gray-200">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="nova-senha"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordFormData.novaSenha}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, novaSenha: e.target.value })}
                    className="bg-[#2a2a2a] border-gray-600 text-white placeholder:text-gray-500 pr-10"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmar-senha" className="text-gray-200">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="confirmar-senha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordFormData.confirmarSenha}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmarSenha: e.target.value })}
                    className="bg-[#2a2a2a] border-gray-600 text-white placeholder:text-gray-500 pr-10"
                    placeholder="Digite a senha novamente"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Validação visual */}
              {passwordFormData.novaSenha && passwordFormData.confirmarSenha && (
                <div className="text-sm">
                  {passwordFormData.novaSenha === passwordFormData.confirmarSenha ? (
                    <p className="text-green-400 flex items-center gap-1">
                      ✓ As senhas coincidem
                    </p>
                  ) : (
                    <p className="text-red-400 flex items-center gap-1">
                      ✗ As senhas não coincidem
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordUser(null)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleResetPasswordSubmit}
              className="bg-[#FF3B3B] hover:bg-[#E22F2F] text-white"
            >
              Salvar Nova Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Dispositivos */}
      <Dialog open={!!viewingDevicesUser} onOpenChange={() => setViewingDevicesUser(null)}>
        <DialogContent className="max-w-4xl bg-white" style={{ color: '#000' }}>
          <DialogHeader>
            <DialogTitle className="text-[#111111]">Gerenciar Dispositivos</DialogTitle>
            <DialogDescription className="text-gray-600">
              Visualize e gerencie os dispositivos autorizados para este usuário
            </DialogDescription>
          </DialogHeader>
          
          <div style={{ color: '#000' }}>
            {viewingDevicesUser && (
              <DeviceManagement 
                userId={viewingDevicesUser.id}
                userName={viewingDevicesUser.nome}
              />
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setViewingDevicesUser(null)}
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsuarios;